import { isValid, parseISO } from "date-fns";

const timeOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
const dateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "short", day: "numeric" };
const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};
const numericDateOptions: Intl.DateTimeFormatOptions = { year: "numeric", month: "2-digit", day: "2-digit" };
const numericTimeOptions: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
const numericDateTimeOptions: Intl.DateTimeFormatOptions = { ...numericDateOptions, ...numericTimeOptions };
const fullDateOptions: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
};
const fullDateTimeOptions: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};
const localLanguage: string = navigator.language; // leaving undefined would not always work, see https://bugs.chromium.org/p/chromium/issues/list?q=tolocaledatestring+type%3Abug

export type FormatDateOptions =
  | {
      hideTime?: boolean | null;
      full?: boolean; // overrides the hideTime and the timeOnly
      timeOnly?: boolean; // overrides the hideTime
      /**
       * Optional output mode.
       * - `"locale"`: localized month/day names (default).
       * - `"numeric"`: localized numeric date/time (disallows `full`).
       */
      format?: "locale";
    }
  | {
      hideTime?: boolean | null;
      timeOnly?: boolean; // overrides the hideTime
      full?: false | null | undefined;
      /**
       * Localized numeric date/time (disallows `full`).
       */
      format: "numeric";
    }
  | {
      /**
       * Year only output (no other options allowed).
       */
      format: "year";
      hideTime?: never;
      full?: never;
      timeOnly?: never;
    };

export const formatDateTimeFromISO = (dateTimeISO: string, options: FormatDateOptions = {}): string => {
  try {
    const date: Date = parseISO(dateTimeISO);
    if (!isValid(date)) return dateTimeISO;

    const format = options.format ?? "locale";
    const isNumeric = format === "numeric";
    const isYearOnly = format === "year";

    if (isYearOnly) {
      return String(date.getFullYear());
    }

    const full = options.full ?? false;
    const timeOnly = options.timeOnly ?? false;
    const hideTime = options.hideTime ?? false;

    let intlOptions: Intl.DateTimeFormatOptions;

    if (full) {
      intlOptions = hideTime ? fullDateOptions : fullDateTimeOptions;
    } else if (timeOnly) {
      intlOptions = isNumeric ? numericTimeOptions : timeOptions;
    } else if (hideTime) {
      intlOptions = isNumeric ? numericDateOptions : dateOptions;
    } else {
      intlOptions = isNumeric ? numericDateTimeOptions : dateTimeOptions;
    }

    return date.toLocaleString(localLanguage, intlOptions);
  } catch {
    return dateTimeISO;
  }
};

export const isMidnightFromISO = (dateTimeISO: string): boolean => {
  try {
    const date: Date = parseISO(dateTimeISO);
    if (!isValid(date)) return false;
    return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;
  } catch {
    return false;
  }
};

export const parseDateTimeToISO = (dateTimeAsString: string): string | null => {
  if (!dateTimeAsString?.trim()) {
    return null;
  }
  const date = new Date(dateTimeAsString);
  return isValid(date) ? date.toISOString() : dateTimeAsString;
};

export const parseDateToISO = (dateTimeAsString: string): string | null => {
  if (!dateTimeAsString?.trim()) {
    return null;
  }
  const date = new Date(dateTimeAsString);
  if (isValid(date)) {
    // we need to keep the offset so that the date corresponds to the local date, before removing the time and timezone information
    const isoDateTimeWithTZOffset = new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString();
    return removeTimeFromISOFormat(isoDateTimeWithTZOffset);
  }
  return dateTimeAsString;
};

export const removeTimeFromISOFormat = (dateTimeISO: string | undefined | null): string | null => {
  if (!dateTimeISO?.trim()) {
    return null;
  }

  return dateTimeISO.slice(0, 10);
};

export const isDateTimeISOFormat = (dateTimeAsString?: string | null): boolean => {
  try {
    if (typeof dateTimeAsString === "string") {
      const date = parseISO(dateTimeAsString);
      return isValid(date);
    }

    return false;
  } catch {
    return false;
  }
};
